import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import React, { useMemo, useState } from "react";
import PageHeader from "../components/PageHeader";
import { BULK_INVITE_WITH_USER_IDS } from "../gql/mutations/event";
import { MEMBER_EVENTS_REVIEW } from "../gql/queries/eventMatching";
import {
  RECORD_RETENTION_OUTREACH,
  RETENTION_ATTENDANCE_DETAIL,
  RETENTION_COMEBACK,
  RETENTION_PREPARE,
} from "../gql/queries/retention";
import { ZONE_COLOURS } from "../helpers/const";
import { Spacing } from "../types/enum";
import { type ZoneLabel } from "../types/graphql";

const FADERS = [
  { key: "Detected", emoji: "🟡", cadence: "Weekly" },
  { key: "Dormant", emoji: "🟠", cadence: "Fortnightly" },
  { key: "Critical", emoji: "🔴", cadence: "Monthly" },
];

const EVENT_ID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
/** Pull the event UUID out of a pasted app/admin event link (or a bare id). */
const extractEventId = (input: string): string =>
  (input.match(EVENT_ID_RE)?.[0] ?? "").toLowerCase();

/** Pull the email addresses out of an uploaded Chargebee CSV (any column). */
const parseEmailsFromCsv = (text: string): string[] => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) {
    return [];
  }
  const cells = (line: string) => line.split(",").map((c) => c.replace(/^"|"$/g, "").trim());
  const header = cells(lines[0]).map((h) => h.toLowerCase());
  const emailIdx = header.findIndex((h) => h === "email" || h.includes("email"));
  const out = new Set<string>();
  const startRow = emailIdx >= 0 ? 1 : 0;
  for (let i = startRow; i < lines.length; i += 1) {
    const row = cells(lines[i]);
    const candidates = emailIdx >= 0 ? [row[emailIdx]] : row;
    for (const c of candidates) {
      if (c?.includes("@")) {
        out.add(c.toLowerCase());
      }
    }
  }
  return [...out];
};

/** Download rows as a CSV file (BOM so Excel reads UTF-8). Ports retDownloadCsv. */
const downloadCsv = (filename: string, rows: string[][]): void => {
  const esc = (v: string) => `"${(v ?? "").replace(/"/g, '""')}"`;
  const csv = rows.map((r) => r.map(esc).join(",")).join("\r\n");
  const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

/** The ready-made Claude prompt for the personalised re-engagement DMs — ported
 *  verbatim from the member tools' retC2Prompt. */
const buildDmPrompt = (faderLabel: string, name: string, date: string, link: string): string =>
  "I'm sending personalised re-engagement DMs to Hey Lady! members who have gone quiet " +
  `(${faderLabel} risk). They got a bulk event invite recently but didn't return, so this is a ` +
  "warm, personal nudge to register for this month's challenge.\n\n" +
  "I've attached a CSV of members with name, email, and colour zone. Write a message for " +
  "EVERY member in the list.\n\nThis is a warm follow-up from a real person, not a first " +
  "hello — don't re-introduce Hey Lady.\n\nFor each member, write a short, warm @tag DM " +
  "(2-4 sentences) that:\n" +
  '  - Begins by addressing her with an @ mention of her FIRST name only, like "@Maria"\n' +
  "  - Opens by wanting to SHARE/SHOW her something rather than saying \"I've been thinking " +
  'of you" — e.g. "I wanted to show you this" or "I\'d love to invite you to this" 💛\n' +
  `  - Warmly invites her to register for this month's challenge: ${name || "[challenge name]"}, starting ${date || "[start date]"}\n` +
  "  - Adds one warm, personal line about why she'll enjoy it\n" +
  "  - Explains that once she registers she can join any daily session that fits her schedule " +
  "— pick one or a few, no pressure, nothing to prepare\n" +
  `  - Includes the registration link: ${link || "[registration link]"}\n` +
  "  - Uses a few warm emojis (1-3), no em dashes, no pressure\n\n" +
  "Split each message into its natural short paragraphs (a sentence or two each) — the " +
  "@mention greeting first, the registration link in its own paragraph at the end — so each " +
  "paragraph can be sent as a separate, tidy chat message.\n\n" +
  "Output: a file (Excel or CSV) with columns [Name | Email | Message 1 | Message 2 | " +
  "Message 3 | Message 4], putting ONE paragraph per Message column in order (leave any " +
  "unused Message columns blank). Keep the Email column exactly as given so I can match the " +
  "rows back.";

const ZoneChip: React.FC<{ zone?: ZoneLabel | null }> = ({ zone }) =>
  zone ? (
    <Chip
      size="small"
      label={zone}
      sx={{ backgroundColor: ZONE_COLOURS.get(zone), color: "#fff", fontWeight: 600 }}
    />
  ) : (
    <Typography component="span" variant="body2" color="textSecondary">
      —
    </Typography>
  );

interface Group {
  eventId: string;
  eventTitle: string;
  zone?: ZoneLabel | null;
  startDate?: string | number | null;
  emails: string[];
  userIds: string[];
}

interface Cycle2Member {
  userId: string;
  name: string;
  email: string;
  zone?: ZoneLabel | null;
  route: string;
}

interface Unmatched {
  userId: string;
  name: string;
  email: string;
  zone?: ZoneLabel | null;
  route: string;
}

interface ComebackRow {
  userId: string;
  name: string;
  email: string;
  country?: string | null;
  plan?: string | null;
  joinDate?: string | null;
  zone?: ZoneLabel | null;
  entrySegment: string;
  entryFader?: string | null;
  currentFader?: string | null;
  subscriptionStatus?: string | null;
  cycles: string;
  dates: string;
  outreachCount: number;
  firstOutreach?: string | null;
  cameBack: boolean;
  comebackDate?: string | null;
  daysToComeback?: number | null;
  comebackEvent?: string | null;
  comebackKind?: string | null;
  comebackHost?: string | null;
  comebackHostBadge?: string | null;
  loggedInSince: boolean;
  cancelled: boolean;
  lastLoginThen?: string | null;
  lastLoginNow?: string | null;
  lastAttendanceThen?: string | null;
  lastAttendanceNow?: string | null;
}

interface AttendanceRow {
  member: string;
  email: string;
  event: string;
  kind?: string | null;
  hostName?: string | null;
  hostBadge?: string | null;
  attended?: string | null;
}

const Kpi: React.FC<{ value: number; label: string }> = ({ value, label }) => (
  <Box
    sx={{
      flex: "1 1 120px",
      textAlign: "center",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      py: 1.5,
    }}
  >
    <Typography variant="h4" color="textPrimary">
      {value}
    </Typography>
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
  </Box>
);

interface CardData {
  key: string;
  title: string;
  zone?: ZoneLabel | null;
  zonesText?: string; // for the unmatched card, which spans several zones
  startDate?: string | number | null;
  members: { userId: string; email: string }[];
  /** Present for auto-matched groups; absent for the "no matched event" card. */
  matchedEventId?: string;
}

/** An upcoming event that could be offered to a group. */
interface Suggestion {
  eventId: string;
  title: string;
  zone?: ZoneLabel | null;
  startDate?: string | number | null;
  hostName?: string | null;
  spotsLeft?: number | null;
}

/** How many suggestions a card offers before it's just another long list. */
const MAX_SUGGESTIONS = 6;

/**
 * The events worth offering this group, best first.
 *
 * Same-zone events come first because the zone *is* the members' local evening —
 * an event outside it is one they'd have to get up at 3am for. Others follow
 * rather than being hidden, since a near-zone event at a civilised hour beats no
 * event at all, which is what the old "paste a link" box left you with.
 */
const suggestionsFor = (
  all: Suggestion[],
  zones: (ZoneLabel | null | undefined)[],
): Suggestion[] => {
  const wanted = new Set(zones.filter(Boolean).map(String));
  const score = (s: Suggestion) => (s.zone && wanted.has(String(s.zone)) ? 0 : 1);
  return [...all]
    .sort((a, b) => {
      const byZone = score(a) - score(b);
      if (byZone !== 0) {
        return byZone;
      }
      return new Date(a.startDate ?? 0).getTime() - new Date(b.startDate ?? 0).getTime();
    })
    .slice(0, MAX_SUGGESTIONS);
};

/**
 * One Cycle 1 group — a per-zone bulk invite to a matched event. Shows the
 * members and lets you override the matched event (or pick one for the
 * unmatched). Sending is done by the single "Invite all" button, mirroring the
 * member tools' one-shot Cycle 1 send.
 */
const Cycle1Card: React.FC<{
  data: CardData;
  link: string;
  onLink: (value: string) => void;
  /** Upcoming events to offer, so an unmatched group isn't a dead end. */
  suggestions: Suggestion[];
}> = ({ data, link, onLink, suggestions }) => {
  const [showMembers, setShowMembers] = useState(false);
  const isUnmatched = !data.matchedEventId;
  const chosenId = extractEventId(link);
  const hasEvent = Boolean(chosenId || data.matchedEventId);
  const count = data.members.length;

  // The zones this card covers — one for a matched group, several for the
  // unmatched catch-all.
  const zones = data.zonesText
    ? (data.zonesText.split(",").map((z) => z.trim()) as unknown as ZoneLabel[])
    : [data.zone];
  const offered = suggestionsFor(suggestions, zones);

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: isUnmatched && !hasEvent ? "warning.light" : "divider",
        borderRadius: 2,
        p: Spacing.m,
        mb: Spacing.s,
      }}
    >
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        {data.zonesText ? (
          <Typography variant="body2" color="textSecondary">
            zones: {data.zonesText}
          </Typography>
        ) : (
          <ZoneChip zone={data.zone} />
        )}
        <Typography variant="body2" fontWeight={600}>
          {isUnmatched ? "⚠️ No matched event — pick one for them" : data.title || "(event)"}
        </Typography>
        {data.startDate ? (
          <Typography variant="caption" color="textSecondary">
            {format(new Date(data.startDate), "EEE d MMM, HH:mm")}
          </Typography>
        ) : null}
        <Chip size="small" label={`${count} member${count === 1 ? "" : "s"}`} />
      </Box>

      {/* Pick from what's actually on, rather than going away to find a link
          and coming back. Shown whenever there's nothing matched, and behind a
          nudge otherwise so an override is still one click. */}
      {offered.length > 0 && (
        <Box mt={1}>
          <Typography variant="caption" color="textSecondary">
            {isUnmatched ? "Suggested events — click one to use it:" : "Or swap to:"}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {offered.map((event) => {
              const inZone = zones.some((z) => z && String(z) === String(event.zone));
              return (
                <Chip
                  key={event.eventId}
                  size="small"
                  clickable
                  color={chosenId === event.eventId ? "primary" : "default"}
                  variant={chosenId === event.eventId ? "filled" : "outlined"}
                  onClick={() => onLink(chosenId === event.eventId ? "" : event.eventId)}
                  label={
                    `${event.title}` +
                    (event.startDate
                      ? ` · ${format(new Date(event.startDate), "EEE d MMM HH:mm")}`
                      : "") +
                    (event.zone ? ` · ${event.zone}${inZone ? "" : " (other zone)"}` : "") +
                    (event.spotsLeft != null ? ` · ${event.spotsLeft} left` : "")
                  }
                  sx={{ maxWidth: "100%", height: "auto", py: 0.5 }}
                />
              );
            })}
          </Stack>
        </Box>
      )}

      <Box mt={1}>
        <TextField
          size="small"
          fullWidth
          value={link}
          onChange={(e) => onLink(e.target.value)}
          placeholder="https://app.heylady.io/event/…"
          label={isUnmatched ? "…or paste an event link" : "Event link — override (optional)"}
        />
      </Box>

      <Button
        size="small"
        onClick={() => setShowMembers((s) => !s)}
        sx={{ mt: 0.5, textTransform: "none" }}
      >
        {showMembers ? "Hide members" : `Show ${count} member${count === 1 ? "" : "s"}`}
      </Button>
      <Collapse in={showMembers}>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: "block", wordBreak: "break-word", pb: 0.5 }}
        >
          {data.members.map((m) => m.email).join(", ")}
        </Typography>
      </Collapse>
    </Box>
  );
};

/**
 * Cycle 2 / Returning — the personalised DM handoff. Instead of sending here,
 * you download the member list + a ready-made Claude prompt, run Claude, then
 * upload its file to Send Messages. Mirrors the member tools' Cycle 2 flow.
 */
const DmHandoff: React.FC<{ members: Cycle2Member[]; faderLabel: string }> = ({
  members,
  faderLabel,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [link, setLink] = useState("");
  const [showPrompt, setShowPrompt] = useState(false);
  const prompt = buildDmPrompt(faderLabel, name, date, link);

  const onCsv = () => {
    const rows: string[][] = [
      ["Name", "Email", "Zone"],
      ...members.map((m) => [m.name, m.email, (m.zone as string) ?? ""]),
    ];
    downloadCsv("retention_dm_members.csv", rows);
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      enqueueSnackbar("Claude prompt copied.", { variant: "success" });
    } catch {
      setShowPrompt(true);
      enqueueSnackbar("Copy failed — the prompt is shown below to copy manually.", {
        variant: "info",
      });
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={Spacing.s} maxWidth="70ch">
        {members.length} member(s) due a personalised DM. Enter this month's challenge, download the
        list, run Claude with the prompt, then upload Claude's file in{" "}
        <strong>Chat Automations → Send Messages</strong> to send the DMs.
      </Typography>
      <Stack direction="row" spacing={Spacing.s} mb={Spacing.s} flexWrap="wrap">
        <TextField
          size="small"
          label="Challenge name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          size="small"
          label="Start date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <TextField
          size="small"
          label="Registration link"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          sx={{ flex: "1 1 260px" }}
        />
      </Stack>
      <Stack direction="row" spacing={Spacing.s} mb={Spacing.s} flexWrap="wrap">
        <Button size="small" variant="outlined" onClick={onCsv}>
          ⬇ Download list (CSV)
        </Button>
        <Button size="small" variant="outlined" onClick={onCopy}>
          📋 Copy Claude prompt
        </Button>
        <Button
          size="small"
          onClick={() => setShowPrompt((s) => !s)}
          sx={{ textTransform: "none" }}
        >
          {showPrompt ? "Hide prompt" : "Show prompt"}
        </Button>
      </Stack>
      <Collapse in={showPrompt}>
        <TextField
          multiline
          fullWidth
          minRows={6}
          value={prompt}
          size="small"
          InputProps={{ readOnly: true }}
        />
      </Collapse>
    </Box>
  );
};

/**
 * The monthly comeback / impact report — did the members we reached out to come
 * back? Load a month, see the roster + comeback rate, download it for Claude.
 */
/** The per-member outcome for the on-screen table (priority order). */
const outcomeOf = (
  r: ComebackRow,
): { label: string; color: "success" | "info" | "error" | "default" } => {
  if (r.cameBack) {
    return { label: "Came back", color: "success" };
  }
  if (r.cancelled) {
    return { label: "Cancelled", color: "error" };
  }
  if (r.loggedInSince) {
    return { label: "Interested", color: "info" };
  }
  return { label: "No response", color: "default" };
};

const ComebackReport: React.FC = () => {
  const now = new Date();
  const defaultMonth = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [load, { data, loading }] = useLazyQuery(RETENTION_COMEBACK, {
    fetchPolicy: "network-only",
  });
  const [loadAttendance] = useLazyQuery(RETENTION_ATTENDANCE_DETAIL, {
    fetchPolicy: "network-only",
  });
  const report = data?.adminRetentionComeback;

  // Sheet 1 — General Info: the roster (entry vs current fader, plan, then/now).
  const onGeneralCsv = () => {
    if (!report) {
      return;
    }
    const rows: string[][] = [
      [
        "Member",
        "Email",
        "Entry Fader",
        "Current Fader",
        "Colour Zone",
        "Plan",
        "Country",
        "Join Date",
        "Outreach Cycles",
        "Outreach Dates",
        "Total Outreaches",
        "Last Login (then)",
        "Last Login (now)",
        "Logged In Since",
        "Last Attendance (then)",
        "Last Attendance (now)",
        "Came Back",
        "Cancelled",
        "Subscription Status",
      ],
      ...report.rows.map((r: ComebackRow) => [
        r.name,
        r.email,
        r.entryFader ?? "",
        r.currentFader ?? "",
        (r.zone as string) ?? "",
        r.plan ?? "",
        r.country ?? "",
        r.joinDate ?? "",
        r.cycles,
        r.dates,
        String(r.outreachCount),
        r.lastLoginThen ?? "",
        r.lastLoginNow ?? "",
        r.loggedInSince ? "Yes" : "No",
        r.lastAttendanceThen ?? "",
        r.lastAttendanceNow ?? "",
        r.cameBack ? "Yes" : "No",
        r.cancelled ? "Yes" : "No",
        r.subscriptionStatus ?? "",
      ]),
    ];
    downloadCsv(`retention_general_info_${report.month}.csv`, rows);
  };

  // Sheet 2 — Comeback Detail: whether they came back + the event they came to.
  const onComebackCsv = () => {
    if (!report) {
      return;
    }
    const rows: string[][] = [
      [
        "Member",
        "Email",
        "Entry Segment",
        "Outreach Cycles",
        "Outreach Dates",
        "Came Back",
        "Comeback Date",
        "Days to Comeback",
        "First Comeback Event",
        "First Event Kind",
        "First Event Host",
        "First Event Host Badge",
      ],
      ...report.rows.map((r: ComebackRow) => [
        r.name,
        r.email,
        r.entrySegment,
        r.cycles,
        r.dates,
        r.cameBack ? "Yes" : "No",
        r.comebackDate ?? "",
        r.daysToComeback != null ? String(r.daysToComeback) : "",
        r.comebackEvent ?? "",
        r.comebackKind ?? "",
        r.comebackHost ?? "",
        r.comebackHostBadge ?? "",
      ]),
    ];
    downloadCsv(`retention_comeback_detail_${report.month}.csv`, rows);
  };

  // Sheet 3 — Attendance Detail: the raw per-event attendance feed (fetched now).
  const onAttendanceCsv = async () => {
    const res = await loadAttendance({ variables: { month } });
    const list: AttendanceRow[] = res.data?.adminRetentionAttendanceDetail ?? [];
    const rows: string[][] = [
      ["Member", "Email", "Event", "Kind", "Host", "Host Badge", "Attended"],
      ...list.map((a) => [
        a.member,
        a.email,
        a.event,
        a.kind ?? "",
        a.hostName ?? "",
        a.hostBadge ?? "",
        a.attended ?? "",
      ]),
    ];
    downloadCsv(`retention_attendance_detail_${month}.csv`, rows);
  };

  return (
    <Card sx={{ mt: Spacing.l }}>
      <Box p={Spacing.l}>
        <Typography variant="h6" gutterBottom>
          Monthly comeback report
        </Typography>
        <Typography variant="body2" color="textSecondary" mb={Spacing.m} maxWidth="72ch">
          Of the members you reached out to in a month (and the month before): who attended a call
          since (<strong>Came back</strong>), who only logged in (<strong>Interested</strong>), and
          who <strong>Cancelled</strong>. Download the three sheets for Claude to write up.
        </Typography>
        <Stack
          direction="row"
          spacing={Spacing.s}
          alignItems="center"
          mb={Spacing.m}
          flexWrap="wrap"
        >
          <TextField
            size="small"
            label="Month"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            onClick={() => void load({ variables: { month } })}
            disabled={loading}
          >
            {loading ? "Loading…" : "Load report"}
          </Button>
        </Stack>

        {report ? (
          <>
            <Stack direction="row" spacing={Spacing.s} mb={Spacing.m} flexWrap="wrap">
              <Kpi value={report.total} label="Total reached out" />
              <Kpi value={report.cameBack} label="Came back" />
              <Kpi value={report.interested} label="Interested" />
              <Kpi value={report.cancelled} label="Cancelled" />
            </Stack>

            <Typography variant="body2" color="textSecondary" mb={Spacing.s}>
              Three sheets to download:
            </Typography>
            <Stack direction="row" spacing={Spacing.s} mb={Spacing.m} flexWrap="wrap">
              <Button variant="outlined" onClick={onGeneralCsv}>
                ⬇ General Info (CSV)
              </Button>
              <Button variant="outlined" onClick={onComebackCsv}>
                ⬇ Comeback Detail (CSV)
              </Button>
              <Button variant="outlined" onClick={() => void onAttendanceCsv()}>
                ⬇ Attendance Detail (CSV)
              </Button>
            </Stack>

            <TableContainer sx={{ maxHeight: "50vh" }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell>Entry → now</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Comeback event</TableCell>
                    <TableCell align="center">Days</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.rows.map((r: ComebackRow) => {
                    const outcome = outcomeOf(r);
                    return (
                      <TableRow key={r.userId}>
                        <TableCell>
                          {r.name}
                          <Typography variant="caption" color="textSecondary" display="block">
                            {r.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="textSecondary">
                            {r.entryFader ?? "—"} → {r.currentFader ?? "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color={outcome.color} label={outcome.label} />
                        </TableCell>
                        <TableCell>
                          {r.comebackEvent ?? "—"}
                          {r.comebackKind ? (
                            <Typography variant="caption" color="textSecondary" display="block">
                              {r.comebackKind}
                              {r.comebackHost ? ` · ${r.comebackHost}` : ""}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="center">{r.daysToComeback ?? "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : loading ? null : (
          <Alert severity="info">Pick a month and load the report.</Alert>
        )}
      </Box>
    </Card>
  );
};

const RetentionPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [fader, setFader] = useState("Detected");
  // Per-group event link (override for matched, chosen for unmatched).
  const [links, setLinks] = useState<Record<string, string>>({});
  const [inviting, setInviting] = useState(false);
  const [invitedCount, setInvitedCount] = useState<number | null>(null);
  // Critical only: the uploaded Chargebee "returning" list.
  const [returningEmails, setReturningEmails] = useState<string[]>([]);
  const [returningFile, setReturningFile] = useState("");

  const [prepare, { data, loading }] = useLazyQuery(RETENTION_PREPARE, {
    fetchPolicy: "network-only",
    onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
  });
  const [invite] = useMutation(BULK_INVITE_WITH_USER_IDS);
  const [recordOutreach] = useMutation(RECORD_RETENTION_OUTREACH);

  // Upcoming events, so a group with no automatic match can be given one from
  // here instead of sending you off to find a link and come back.
  const { data: eventsData } = useQuery(MEMBER_EVENTS_REVIEW, {
    variables: { days: 21 },
    fetchPolicy: "cache-first",
  });
  const suggestions: Suggestion[] = useMemo(
    () =>
      (eventsData?.adminMemberEventsReview ?? []).map(
        (event: {
          eventId: string;
          title: string;
          zone?: ZoneLabel | null;
          startDate?: string | null;
          hostName?: string | null;
          spotsLeft?: number | null;
        }) => ({
          eventId: event.eventId,
          title: event.title,
          zone: event.zone,
          startDate: event.startDate,
          hostName: event.hostName,
          spotsLeft: event.spotsLeft,
        }),
      ),
    [eventsData],
  );

  const result = data?.adminRetentionPrepare;
  const meta = FADERS.find((f) => f.key === fader) ?? FADERS[0];
  const isCritical = result?.fader === "Critical";

  const onPrepare = () => {
    setLinks({});
    setInvitedCount(null);
    void prepare({
      variables: { fader, returningEmails: fader === "Critical" ? returningEmails : undefined },
    });
  };

  const onReturningFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const emails = parseEmailsFromCsv(await file.text());
    setReturningEmails(emails);
    setReturningFile(file.name);
    enqueueSnackbar(`Loaded ${emails.length} returning email(s) — press Prepare to apply.`, {
      variant: "success",
    });
    // Allow re-selecting the same file later.
    event.target.value = "";
  };

  // Every Cycle 1 group that has an event (matched or pasted), as a send target.
  const targets = useMemo(() => {
    if (!result) {
      return [] as { eventId: string; userIds: string[] }[];
    }
    const out: { eventId: string; userIds: string[] }[] = [];
    for (const g of result.cycle1.groups as Group[]) {
      const eventId = extractEventId(links[g.eventId] ?? "") || g.eventId;
      if (eventId && g.userIds.length) {
        out.push({ eventId, userIds: g.userIds });
      }
    }
    if (result.cycle1.unmatched.length) {
      const eventId = extractEventId(links.unmatched ?? "");
      if (eventId) {
        out.push({ eventId, userIds: result.cycle1.unmatched.map((m: Unmatched) => m.userId) });
      }
    }
    return out;
  }, [result, links]);

  const totalToInvite = targets.reduce((n, t) => n + t.userIds.length, 0);

  const onInviteAll = async () => {
    if (targets.length === 0) {
      return;
    }
    setInviting(true);
    let ok = 0;
    let fail = 0;
    for (const t of targets) {
      try {
        await invite({ variables: { eventId: t.eventId, input: { userIds: t.userIds } } });
        // Record the send so next run advances these members to Cycle 2.
        await recordOutreach({
          variables: { userIds: t.userIds, fader: result?.fader ?? fader, eventId: t.eventId },
        });
        ok += t.userIds.length;
      } catch {
        fail += t.userIds.length;
      }
    }
    setInviting(false);
    setInvitedCount(ok);
    enqueueSnackbar(`Invited ${ok} member(s)${fail ? `, ${fail} failed` : ""}.`, {
      variant: ok > 0 ? "success" : "error",
    });
  };

  return (
    <>
      <PageHeader title="Retention">
        Pull the current <strong>{fader}</strong> members, auto-route each to{" "}
        <strong>Cycle 1</strong> (per-zone bulk invite to the closest matched event) or{" "}
        <strong>Cycle 2</strong> (personalised DM) based on their outreach history, then invite
        everyone in one go. Every action is recorded to the outreach log so no one is contacted
        twice.
      </PageHeader>

      <Card>
        <Box p={Spacing.l}>
          <Stack
            direction="row"
            spacing={Spacing.m}
            alignItems="center"
            mb={Spacing.m}
            flexWrap="wrap"
          >
            <ToggleButtonGroup
              size="small"
              exclusive
              value={fader}
              onChange={(_e, value) => {
                if (value) {
                  // Clear the previous cohort so switching tabs shows a blank
                  // state — the operator must Prepare each cohort deliberately.
                  setFader(value);
                  setLinks({});
                  setInvitedCount(null);
                }
              }}
            >
              {FADERS.map((f) => (
                <ToggleButton key={f.key} value={f.key}>
                  {f.emoji} {f.key} · {f.cadence}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Button variant="contained" onClick={onPrepare} disabled={loading}>
              {loading ? "Preparing…" : "Prepare"}
            </Button>
            {loading ? <CircularProgress size={20} /> : null}
            {fader === "Critical" ? (
              <Button component="label" size="small" variant="outlined">
                {returningFile
                  ? `↺ Returning list (${returningEmails.length})`
                  : "⬆ Upload returning list (CSV)"}
                <input hidden type="file" accept=".csv,text/csv" onChange={onReturningFile} />
              </Button>
            ) : null}
          </Stack>

          {(!result || result.fader !== fader) && !loading ? (
            <Alert severity="info">
              Pick a cohort and press <strong>Prepare</strong> to route this week's {fader} members.
            </Alert>
          ) : null}

          {result && result.fader === fader ? (
            <>
              {result.truncated ? (
                <Alert severity="warning" sx={{ mb: Spacing.m }}>
                  More members are at this risk level than one run handles. This is the first{" "}
                  <strong>{result.cap}</strong>, the ones who have gone quietest — route them, then
                  press Prepare again for the next group.
                </Alert>
              ) : null}
              <Stack direction="row" spacing={Spacing.s} mb={Spacing.l} flexWrap="wrap">
                <Kpi value={result.total} label={`${meta.emoji} ${result.fader}`} />
                <Kpi
                  value={result.cycle1.count}
                  label={isCritical ? "Interested · invite" : "Cycle 1 · invite"}
                />
                <Kpi
                  value={result.cycle2.count}
                  label={isCritical ? "Returning · DM" : "Cycle 2 · DM"}
                />
                {isCritical ? (
                  <Kpi value={result.excluded ?? 0} label="Gone · excluded" />
                ) : (
                  <Kpi value={result.waiting} label="Waiting" />
                )}
              </Stack>

              {/* Cycle 1 / Interested */}
              <Typography variant="h6" gutterBottom>
                {isCritical
                  ? "Interested — per-zone bulk invite"
                  : "Cycle 1 — per-zone bulk invite"}
              </Typography>
              {result.cycle1.count > 0 ? (
                <Typography variant="caption" color="textSecondary" display="block" mb={Spacing.s}>
                  Matched: <strong>{result.cycle1.matchTiers.featured}</strong> to featured ·{" "}
                  <strong>{result.cycle1.matchTiers.coach}</strong> coach (by level) ·{" "}
                  <strong>{result.cycle1.matchTiers.cxt}</strong> CXT-other ·{" "}
                  <strong>{result.cycle1.matchTiers.none}</strong> need an event manually. (coaching
                  plan → coach events at their level; social plan → CXT; featured first when nearby;
                  ±1 zone)
                </Typography>
              ) : null}

              {result.cycle1.groups.length > 0 || result.cycle1.unmatched.length > 0 ? (
                <>
                  <Box mb={Spacing.s}>
                    {result.cycle1.groups.map((g: Group) => (
                      <Cycle1Card
                        key={g.eventId}
                        link={links[g.eventId] ?? ""}
                        suggestions={suggestions}
                        onLink={(value) => setLinks((prev) => ({ ...prev, [g.eventId]: value }))}
                        data={{
                          key: g.eventId,
                          title: g.eventTitle,
                          zone: g.zone,
                          startDate: g.startDate,
                          matchedEventId: g.eventId,
                          members: g.emails.map((email, i) => ({ userId: g.userIds[i], email })),
                        }}
                      />
                    ))}
                    {result.cycle1.unmatched.length > 0 ? (
                      <Cycle1Card
                        link={links.unmatched ?? ""}
                        suggestions={suggestions}
                        onLink={(value) => setLinks((prev) => ({ ...prev, unmatched: value }))}
                        data={{
                          key: "unmatched",
                          title: "",
                          zonesText:
                            [
                              ...new Set(
                                result.cycle1.unmatched
                                  .map((m: Unmatched) => m.zone)
                                  .filter(Boolean),
                              ),
                            ].join(", ") || "—",
                          members: result.cycle1.unmatched.map((m: Unmatched) => ({
                            userId: m.userId,
                            email: m.email,
                          })),
                        }}
                      />
                    ) : null}
                  </Box>

                  <Stack direction="row" spacing={Spacing.m} alignItems="center" mb={Spacing.l}>
                    <Button
                      variant="contained"
                      onClick={onInviteAll}
                      disabled={inviting || totalToInvite === 0}
                    >
                      {inviting
                        ? "Inviting…"
                        : `Invite all Cycle 1 (${totalToInvite} member${totalToInvite === 1 ? "" : "s"})`}
                    </Button>
                    {inviting ? <CircularProgress size={20} /> : null}
                    {invitedCount !== null ? (
                      <Typography variant="body2" color="success.main">
                        ✓ Invited {invitedCount} member(s).
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="textSecondary">
                        Invites every group above to its event in one go.
                      </Typography>
                    )}
                  </Stack>
                </>
              ) : (
                <Typography variant="body2" color="textSecondary" mb={Spacing.m}>
                  No Cycle 1 members this run.
                </Typography>
              )}

              {/* Cycle 2 / Returning — DM handoff */}
              <Typography variant="h6" gutterBottom>
                {isCritical ? "Returning — welcome-back DM" : "Cycle 2 — personalised DM"}
              </Typography>
              {result.cycle2.count > 0 ? (
                <DmHandoff members={result.cycle2.members} faderLabel={result.fader} />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  {isCritical
                    ? "No Returning members yet — upload a Chargebee returning list to populate this."
                    : "No Cycle 2 members this run."}
                </Typography>
              )}
            </>
          ) : null}
        </Box>
      </Card>

      <ComebackReport />
    </>
  );
};

export default RetentionPage;
